import { Rental, RentalStatus } from '../../../services/models/rental';
import { TimelineItem } from '../../../components/Timeline/Timeline';

/** Lane group for rentals whose equipment carries no lock info. */
export const NO_LOCK_KEY = Number.MAX_SAFE_INTEGER;

export interface RentalLockItem extends TimelineItem {
  rental: Rental;
  /** 0-based lock position (row key), or NO_LOCK_KEY when unknown. */
  lockPosition: number;
}

/**
 * Expands rentals into one timeline item per (rental, lock) pair so each lock
 * gets its own row: a 3-lock rental yields 3 vertically aligned bars. Two
 * equipments of one rental on the same lock are deduped into a single bar, and
 * rentals with no lock info fall into the NO_LOCK_KEY group instead of being
 * silently dropped.
 */
export function expandRentalsToLockItems(rentals: Rental[]): RentalLockItem[] {
  return rentals.flatMap((rental) => {
    const start = rental.startDateUTC ? new Date(rental.startDateUTC) : null;
    const end = rental.endDateUTC ? new Date(rental.endDateUTC) : null;
    const positions = new Set<number>(
      (rental.equipments ?? [])
        .map((equipment) => equipment.lock?.position)
        .filter((position): position is number => position != null),
    );

    if (positions.size === 0) {
      return [{ id: `r-${rental.id}:none`, start, end, rental, lockPosition: NO_LOCK_KEY }];
    }
    return Array.from(positions).map((position) => ({
      id: `r-${rental.id}:${position}`,
      start,
      end,
      rental,
      lockPosition: position,
    }));
  });
}

/**
 * Time ranges where two different rentals overlap on the same lock — a real
 * double-booking. CLOSED rentals are ignored (a closed rental no longer
 * reserves its lock, mirroring the API's overlap guard), and overlapping
 * ranges are merged so each problem window renders as one band.
 */
export function findLockConflictRanges(items: RentalLockItem[]): { start: Date; end: Date }[] {
  const byLock = new Map<number, RentalLockItem[]>();
  for (const item of items) {
    if (item.lockPosition === NO_LOCK_KEY) continue;
    if (item.rental.rentalStatus === RentalStatus.CLOSED) continue;
    const group = byLock.get(item.lockPosition);
    if (group) group.push(item);
    else byLock.set(item.lockPosition, [item]);
  }

  const startMs = (item: RentalLockItem) => (item.start ? item.start.getTime() : -Infinity);
  const endMs = (item: RentalLockItem) => (item.end ? item.end.getTime() : Infinity);
  const ranges: { start: number; end: number }[] = [];

  byLock.forEach((lockItems) => {
    const sorted = [...lockItems].sort((a, b) => startMs(a) - startMs(b));
    for (let i = 0; i < sorted.length; i += 1) {
      for (let j = i + 1; j < sorted.length; j += 1) {
        if (sorted[i].rental.id === sorted[j].rental.id) continue;
        if (startMs(sorted[j]) >= endMs(sorted[i])) break;
        const start = Math.max(startMs(sorted[i]), startMs(sorted[j]));
        const end = Math.min(endMs(sorted[i]), endMs(sorted[j]));
        if (end > start) ranges.push({ start, end });
      }
    }
  });

  ranges.sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged.map((range) => ({ start: new Date(range.start), end: new Date(range.end) }));
}
